import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { BottomNav } from "@/components/shared/Navigation";
import {
  ArrowLeft, MapPin, TrendingUp, Loader2, Globe, Gift,
  ChevronRight, Eye, Heart, ClipboardCheck,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { trackPageView } from "@/lib/track";

// Fix Leaflet default icon issue with webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const trendingIcon = new L.DivIcon({
  className: "custom-trending-pin",
  html: `<div style="
    width:32px;height:32px;border-radius:50%;
    background:linear-gradient(135deg,#ec4899,#f43f5e);
    border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);
    display:flex;align-items:center;justify-content:center;
  "><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg></div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -34],
});

function RecenterMap({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView([center.lat, center.lng], 10);
  }, [center, map]);
  return null;
}

const OfferPopup = ({ marker, onNavigate }) => (
  <div className="w-52">
    <div className="flex items-center gap-2 mb-1.5">
      {marker.logo_url ? (
        <img src={marker.logo_url} alt="" className="w-7 h-7 rounded-lg object-contain bg-slate-50" />
      ) : (
        <Gift className="w-5 h-5 text-slate-400" />
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-slate-800 truncate">{marker.name}</p>
        <p className="text-[10px] text-slate-400">{marker.category}</p>
      </div>
    </div>
    <p className="text-xs text-slate-600 mb-1.5 line-clamp-2">{marker.offer_title}</p>
    <div className="flex items-center gap-3 text-[10px] text-slate-400 mb-2">
      <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{marker.view_count}</span>
      <span className="flex items-center gap-0.5"><ClipboardCheck className="w-3 h-3" />{marker.claim_count}</span>
    </div>
    {marker.offer_value_est > 0 && (
      <p className="text-xs font-bold text-green-600 mb-2">${marker.offer_value_est} value</p>
    )}
    <button
      onClick={() => onNavigate(`/offer/${marker.business_id}`)}
      className="w-full text-xs font-semibold text-white bg-pink-500 hover:bg-pink-600 rounded-lg py-1.5 transition-colors"
      data-testid={`map-popup-view-${marker.business_id}`}
    >
      View Deal
    </button>
  </div>
);

const NationwideCard = ({ offer, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-2.5 p-2.5 rounded-xl bg-white border border-slate-100 hover:shadow-sm transition-shadow text-left"
    data-testid={`nationwide-card-${offer.business_id}`}
  >
    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-slate-50 flex items-center justify-center">
      {offer.logo_url ? (
        <img src={offer.logo_url} alt="" className="w-full h-full object-contain" />
      ) : (
        <Gift className="w-4 h-4 text-slate-400" />
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs font-semibold text-slate-700 truncate">{offer.name}</p>
      <p className="text-[10px] text-slate-400 truncate">{offer.offer_title}</p>
    </div>
    {offer.offer_value_est > 0 && (
      <span className="text-xs font-bold text-green-600 flex-shrink-0">${offer.offer_value_est}</span>
    )}
    <ChevronRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
  </button>
);

export default function TrendingMapPage() {
  const navigate = useNavigate();
  const { geo } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackPageView("trending_map");
    (async () => {
      try {
        let url = `${API}/digest/trending-map`;
        const params = [];
        if (geo?.coords) {
          params.push(`lat=${geo.coords.lat}`, `lng=${geo.coords.lng}`);
        }
        if (params.length) url += `?${params.join("&")}`;
        const res = await axios.get(url);
        setData(res.data);
      } catch (e) {
        console.error("Trending map error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [geo?.coords]);

  // Deduplicate markers by business_id (keep best one per business for cleaner map)
  const uniqueMarkers = useMemo(() => {
    if (!data?.markers) return [];
    const seen = new Map();
    for (const m of data.markers) {
      if (!seen.has(m.business_id) || m.trending_score > seen.get(m.business_id).trending_score) {
        seen.set(m.business_id, m);
      }
    }
    return Array.from(seen.values());
  }, [data?.markers]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-pink-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-400">Failed to load map data</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: "var(--theme-page-bg, #ffffff)" }} data-testid="trending-map-page">
      {/* Header */}
      <div className="sticky top-0 z-[400] bg-white/90 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate("/weather-report")}
          className="p-1.5 hover:bg-slate-100 rounded-lg"
          data-testid="map-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <MapPin className="w-5 h-5 text-pink-500" />
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-slate-800">Trending Map</h1>
          <p className="text-[10px] text-slate-400">{data.total_trending} trending near {data.location_context}</p>
        </div>
      </div>

      {/* Map */}
      <div className="relative" style={{ height: "55vh" }} data-testid="map-container">
        <MapContainer
          center={[data.center.lat, data.center.lng]}
          zoom={10}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecenterMap center={data.center} />
          {uniqueMarkers.map((m, i) => (
            <Marker
              key={`${m.business_id}-${i}`}
              position={[m.lat, m.lng]}
              icon={trendingIcon}
            >
              <Popup>
                <OfferPopup marker={m} onNavigate={navigate} />
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Floating legend */}
        <div className="absolute bottom-3 left-3 z-[400] bg-white/95 backdrop-blur-sm rounded-xl px-3 py-2 shadow-lg border border-slate-100">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 border border-white shadow-sm" />
            <span className="text-[10px] font-medium text-slate-600">
              {uniqueMarkers.length} pinned location{uniqueMarkers.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Nationwide offers section (no map pin) */}
      {data.nationwide_offers?.length > 0 && (
        <div className="px-4 pt-4 space-y-2">
          <div className="flex items-center gap-2 px-1">
            <span className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
              <Globe className="w-3.5 h-3.5 text-blue-600" />
            </span>
            <span className="text-sm font-bold text-slate-700">Nationwide Deals</span>
            <span className="text-xs font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
              {data.nationwide_offers.length}
            </span>
          </div>
          <p className="text-[10px] text-slate-400 px-1">Available everywhere — not pinned on map</p>
          <div className="space-y-1.5">
            {data.nationwide_offers.map((o) => (
              <NationwideCard
                key={o.business_id}
                offer={o}
                onClick={() => navigate(`/offer/${o.business_id}`)}
              />
            ))}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}
